import React, { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from 'xlsx';
import FactureModal from "../components/FactureModal";
import OperationModal from "../components/OperationModal";
import RecurrenceModal from "../components/RecurrenceModal";
import RecurrenceMonthsModal from "../components/RecurrenceMonthsModal";
import TransactionRow from "../components/TransactionRow"; 
import FilterBar from "../components/FilterBar";
import { useSelected } from "../contexts/SelectedContext";
import { useRecurrenceFlow } from '../hooks/useRecurrenceFlow'; 
import { Link } from "react-router-dom"; 
import { saveRecurrence, processRecurrences } from '../services/recurrenceService'
import useSubscriptionStatus from '../hooks/useSubscriptionStatus';

import { useNotification } from '../contexts/NotificationContext';

// Import des hooks et constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS, FLOW_TYPES, PAYMENTS_MOD } from "../components/Constants";

import { authFetch } from "../utils/api";

import {
  BarChart, 
  Bar, 
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ===============================================
// ✅ FONCTIONS UTILITAIRES
// ===============================================

const formatCurrency = (amount) => {
  const number = Number(amount);
    
    if (Math.abs(number) < 0.01) {
        return "-";
    }
    return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const sortData = (a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    
    if (dateA.getTime() !== dateB.getTime()) {
      return dateB.getTime() - dateA.getTime(); 
    }
    return b.id - a.id;
};

const getEffectiveAmount = (op, selectedAccountId) => {
    if (op.sourceAccountId && op.destinationAccountId) {
        if (op.sourceAccountId === selectedAccountId) {
            return -Math.abs(Number(op.montant) || 0);
        }
        if (op.destinationAccountId === selectedAccountId) {
            return Math.abs(Number(op.montant) || 0);
        }
        return 0;
    }
    return Number(op.montant || 0);
};

const NON_BANCAIRE_MODES = ["Espèces", "Tickets restaurant", "Autre"];

const getBancaireAmountFromFacture = (facture) => {
    if (facture.bill_payments && facture.bill_payments.length > 0) {
        return facture.bill_payments.reduce((sum, paiement) => {
            if (!NON_BANCAIRE_MODES.includes(paiement.mode)) {
                return sum + Number(paiement.montant || paiement.amount_paid || 0);
            }
            return sum;
        }, 0);
    }
    return Number(facture.montant || 0); 
};

const isBancaire = (op) => {
    if (!op.sourceAccountId && !op.destinationAccountId) {
        return !NON_BANCAIRE_MODES.includes(op.paiementMode);
    }
    return true; 
};

const handleDeleteEntity = async (entityType, id, refreshAllData) => {
    try {
        // 🔑 CORRECTION 1 : GET /check-usage
        const response = await authFetch.get(`/check-usage/${entityType}/${id}`);
        const isUsed = await response.json();

        if (isUsed) {
            alert(`Impossible de supprimer, cette ${entityType} est utilisée.`);
        } else {
            // 🔑 CORRECTION 2 : DELETE /:entityType/:id
            await authFetch.delete(`/${entityType}/${id}`);
            refreshAllData();
        }
    } catch (error) {
        console.error(
            `❌ Erreur lors de la suppression d'une ${entityType} :`,
            error
        );
    }
};

const TAB_STORAGE_KEY = 'dashboardSelectedTab';

function CategoryRow({ category, handleCategoryEdit, handleToggleCategoryVisibility, styles, themeColors, refreshAllData, handleUpdateCategoryType }) {
    // Déterminer si l'on est dans la corbeille (is_hidden est vrai)
    const isInCorbeille = category.is_hidden === 1 || category.is_hidden === true;
    
    return (
        <tr 
            key={category.id}
            style={styles.tableRow} 
        >
            <td style={styles.left}>{category.name}</td>
            
            {/* 💡 NOUVEAU : Colonnes pour les boutons radio */}
            {FLOW_TYPES.map(flowType => (
                <td key={flowType.code} style={styles.center}>
                    <input
                        type="radio"
                        name={`category-type-${category.id}`} // Nom unique pour grouper les radios par catégorie
                        value={flowType.code}
                        checked={category.type === flowType.code}
                        onChange={() => handleUpdateCategoryType(category, flowType.code)}
                        style={{ cursor: 'pointer' }}
                        // Désactive les boutons radio si la catégorie est masquée
                        disabled={isInCorbeille}
                    />
                </td>
            ))}
            {/* FIN NOUVEAU */}

            <td style={styles.center}>
                {/* ✅ CONDITIONNEL : Bouton de Suppression Définitive (Uniquement si masquée) */}
                {isInCorbeille && (
                    <button 
                        onClick={() => {
                            if (window.confirm("Êtes-vous sûr de vouloir supprimer DÉFINITIVEMENT cette catégorie? Cela échouera si elle est utilisée.")) {
                                handleDeleteEntity("categories", category.id,refreshAllData); 
                            }
                        }}
                        // Couleur Danger
                        style={{ ...styles.actionButton, backgroundColor: themeColors.danger, color: themeColors.textSecondary }}
                    >
                        Supprimer
                    </button>
                )}

                <button 
                    onClick={() => handleCategoryEdit(category)}
                    // Couleur Accent
                    style={{ ...styles.actionButton, backgroundColor: themeColors.accent, color: themeColors.text }}
                    disabled={isInCorbeille} // Désactiver l'édition si masquée
                >
                    Modifier
                </button>

                {/* Bouton Masquer / Rétablir (toujours visible) */}
                <button 
                    onClick={() => handleToggleCategoryVisibility(category)}
                    // Couleurs dynamiques
                    style={{ 
                        ...styles.actionButton, 
                        backgroundColor: isInCorbeille ? themeColors.success : themeColors.danger, 
                        color: themeColors.textSecondary 
                    }}
                >
                    {isInCorbeille ? 'Rétablir' : 'Masquer'}
                </button>

            </td>
        </tr>
    );
}

// ===============================================
// ✅ COMPOSANT PRINCIPAL
// ===============================================

export default function DashboardPage({
  currentUser,
  persons,
  accounts,
  categories,
  selectedYear,
  refreshAllData,
  refreshDashboardData,
  dashboardRefreshKey,
}) {
  
  const { selectedPerson, selectedAccount } = useSelected();
  const { notify } = useNotification();

  const { 
      isTrial, 
      isExpired, 
      trialDaysRemaining, 
      plan 
  } = useSubscriptionStatus();

  // Récupération du thème
  const { currentTheme } = useTheme();
  const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

  const [factures, setFactures] = useState([]);
  const [operations, setOperations] = useState([]);
  const [reimbursementFlow, setReimbursementFlow] = useState([]);

  // Onglet sélectionné
  const [selectedTab, setSelectedTab] = useState(() => {
    const savedTab = localStorage.getItem(TAB_STORAGE_KEY);
    return savedTab || "factures";
  });

  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);
    localStorage.setItem(TAB_STORAGE_KEY, newTab); // 🔑 STOCKAGE DANS LOCALSTORAGE
};

  // Filtres
  const [factureFilter, setFactureFilter] = useState({
      startDate: '',
      endDate: '',
      categorieId: '',
      description: '',
      minAmount: '',
      maxAmount: '',
  });

  const [operationFilter, setOperationFilter] = useState({
      startDate: '',
      endDate: '',
      categorieId: '',
      description: '',
      minAmount: '',
      maxAmount: '',
  });

  // Pour gérer l'état 'indeterminate' de la case "tout sélectionner"
  const selectAllCheckboxRef = React.useRef(null);

  // Réf pour la case 'Tout sélectionner'
  const selectAllFacturesRef = useRef(null);
  const selectAllOperationsRef = useRef(null);

  // Modals Facture/Opération
  const [showFactureModal, setShowFactureModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);

  const [expandedFactureId, setExpandedFactureId] = useState(null);

  // États pour la gestion des catégories et des personnes
  const [isCategoryFormVisible, setIsCategoryFormVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null); // Catégorie sélectionnée pour modification
  const [categoryName, setCategoryName] = useState('');
  const [categoryType, setCategoryType] = useState('D');
  const [isPersonFormVisible, setIsPersonFormVisible] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null); // Personne sélectionnée pour modification
  const [personName, setPersonName] = useState('');
  const [isManaged, setIsManaged] = useState(true); // Nouveau: Statut is_managed

  // État pour le solde d'ouverture
  const [openingBalance, setOpeningBalance] = useState(0);

  // Etat pour la sélection des lignes pour les actions groupées
  const [selectedFactureIds, setSelectedFactureIds] = useState([]);
  const [selectedOperationIds, setSelectedOperationIds] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchType, setBatchType] = useState(null);

  const [isFilterBarVisible, setIsFilterBarVisible] = useState(false);

  // Gère l'ouverture/fermeture d'une ligne de facture
  const handleToggleFactureExpand = (factureId) => {
    // Si l'ID cliqué est déjà ouvert, on le ferme (null)
    // Sinon, on ouvre le nouvel ID, ce qui ferme automatiquement l'ancien
    setExpandedFactureId(prevId => (prevId === factureId ? null : factureId));
  };
  
  // Modals Récurrence (état global géré par hook)
  const handleRecurrenceFinalSave = async (finalData) => {
    try {
        await saveRecurrence(finalData);
        notify("Nouvelle récurrence créée avec succès !");

    } catch (error) {
        console.error("❌ Erreur de sauvegarde finale:", error);
        notify(`Erreur lors de la création de la récurrence : ${error.message}`);
    }
    processRecurrences();
    refreshDashboardData();
  };
    
  const {
      showRecurrenceModal,
      showRecurrenceMonthsModal,
      dataForMonthsModal,
      openNewRecurrence,
      handleNextStep,
      handleCloseRecurrenceModal,
      handleCloseMonthsModal,
  } = useRecurrenceFlow({ onFinalSave: handleRecurrenceFinalSave });

  // Fonction pour forcer la vérification et le backfill (identique à RecurrencePage)
    const runRecurrenceCheck = useCallback(async () => {
        if (!currentUser) return;
        try {
            processRecurrences();
            //notify(`${message}`);

            refreshDashboardData(); 
        } catch (error) {
            notify(`⚠️ Échec de la vérification de backfill: ${error.message}`, 'error');
            console.warn(`⚠️ Le processus de récurrence a échoué : ${error.message}`);
        }
    }, [currentUser, notify, refreshDashboardData]);

  // Fetch des données principal
  useEffect(() => {
    if (selectedPerson && selectedAccount && selectedYear) {
      // 🚀 UNE SEULE REQUÊTE BUNDLE
      authFetch.get(`/api/dashboard-bundle/${selectedPerson.id}/${selectedAccount.id}/${selectedYear}`)
        .then(res => res.json())
        .then(data => {
          setFactures(data.factures || []);
          setOperations(data.operations || []);
          setReimbursementFlow(data.reimbursementFlow || []);
          setOpeningBalance(data.openingBalance || 0);
        })
        .catch(err => {
          console.error("Erreur chargement Dashboard:", err);
          notify("Erreur lors du chargement des données", "error");
        });
    }
  }, [selectedPerson, selectedAccount, selectedYear, dashboardRefreshKey, notify]);

    // Appliquer les filtres aux données
  const applyFilters = (data, filters, type) => {
      if (!data || data.length === 0) return [];
      
      return data.filter(item => {
          // Conversion de date pour la comparaison
          const itemDate = new Date(item.date).getTime();
            
          // Détermination du montant effectif pour le filtrage
          let amount = Number(item.montant || 0);
          if (type === 'operation') {
            amount = getEffectiveAmount(item, selectedAccount?.id);
          } else {
              // Pour les factures, on filtre sur le montant total
              amount = Number(item.montant || 0);
          }

          // 1. Filtrage par date de début
          if (filters.startDate) {
              const filterStart = new Date(filters.startDate).getTime();
              if (itemDate < filterStart) return false;
          }

          // 2. Filtrage par date de fin
          if (filters.endDate) {
              const filterEnd = new Date(filters.endDate).getTime();
              if (itemDate > filterEnd) return false;
          }
          
          // 3. Filtrage par catégorie
          if (filters.categorieId) {
              if (Number(item.categorie_id) !== Number(filters.categorieId)) return false;
          }

          // 4. Filtrage par description/libellé
          if (filters.description) {
              const description = item.description || '';
              if (!description.toLowerCase().includes(filters.description.toLowerCase())) return false;
          }

          // 5. Filtrage par montant minimum
          if (filters.minAmount !== '' && !isNaN(parseFloat(filters.minAmount))) {
              if (amount < parseFloat(filters.minAmount)) return false;
          }

          // 6. Filtrage par montant maximum
          if (filters.maxAmount !== '' && !isNaN(parseFloat(filters.maxAmount))) {
              if (amount > parseFloat(filters.maxAmount)) return false;
          }
          
          return true;
      });
  };

  // 💡 FONCTION DE VÉRIFICATION SI DES FILTRES SONT APPLIQUÉS
  const checkFilterActive = (filterState) => {
      return Object.values(filterState).some(value => {
          if (value === '') return false;
          // Pour les IDs (catégorie), vérifie si c'est une valeur positive
          if (typeof value === 'number') return value > 0;
          // Pour les chaînes (dates, descriptions, montants)
          return String(value).trim() !== '';
      });
  };

  // 💡 CALCUL DES FILTRES ACTIFS (Déclaration avant le return)
  const areFactureFiltersActive = checkFilterActive(factureFilter);
  const areOperationFiltersActive = checkFilterActive(operationFilter);
  const areNonPayeesFiltersActive = areFactureFiltersActive

  // Application des filtres et tri
  const sortedFactures = Array.isArray(factures) ? [...factures].sort(sortData) : [];
  const sortedOperations = Array.isArray(operations) ? [...operations].sort(sortData) : [];

  // Application des filtres pour l'affichage dans les tableaux
  const filteredFactures = applyFilters(sortedFactures, factureFilter, 'facture');
  const filteredOperations = applyFilters(sortedOperations, operationFilter, 'operation');
  
  // Récupérer la liste des factures NON PAYÉES ET FILTRÉES pour l'onglet "non-payees"
  const nonPayeesFiltered = filteredFactures.filter((f) => f.statut === "Non payé");
  
  // =========================================================================
  // ✅ CALCUL DES TOTAUX (Logique métier : Facture = Charge, même si créance)
  // =========================================================================

  const selectedAccountId = selectedAccount?.id;
  const selectedPersonId = selectedPerson?.id;

  // 1. TOTAL BANQUE (Opérations totales, y compris non-bancaires, SANS filtre de mode)
  const totalBanque = sortedOperations.reduce((sum, op) => sum + getEffectiveAmount(op, selectedAccountId), 0);

  // 2. TOTAL FACTURES (Dépenses totales de la personne: Factures Payées + Dettes Remb. - Créances Remb.)
  
  // 💡 Accumulation : On accumule le montant TOTAL de la facture (Facture = Charge)
  const totalFacturesPayees = sortedFactures
    .filter((f) => f.statut === "Payé")
    .reduce((sum, f) => {
        const isP1Involved = 
            Number(f.person_id) === selectedPersonId ||
            (f.bill_beneficiaries && f.bill_beneficiaries.some(b => Number(b.beneficiary_person_id) === selectedPersonId));

        if (isP1Involved) {
            const partReelle = Number(f.montant || 0) - Number(f.total_received || 0);
            
            return sum + partReelle;
        }
        return sum;
    }, 0);
        
    //const totalCreancesRemboursees = reimbursementFlow
        // 1. Filtrer pour les créances remboursées à la personne focus
        //.filter(item => Number(item.creditor_person_id) === selectedPersonId)
        // 2. 🔑 CORRECTION CRITIQUE : FILTRER PAR COMPTE DESTINATAIRE
        // La créance est remboursée si l'argent arrive dans ce compte.
        //.filter(item => Number(item.destinationAccountId) === selectedAccountId)
        //.reduce((sum, item) => sum + Number(item.reimbursement_amount || 0), 0);

    // Total Factures = Factures payées - Créances remboursées (sur CE compte)
    const totalFactures = totalFacturesPayees;

  // 3. TOTAL DISPONIBLE (Ce montant est désormais fourni par le backend)
  const totalDisponible = openingBalance; // 🔑 VALEUR CUMULÉE DU BACKEND (-150 € dans l'exemple)

  // --- 4. CALCULS SECONDAIRES (Nécessaires pour l'affichage du Total Non Payé et Total Restant) ---

  // Opérations Non Payées (modes bancaires uniquement)
  const totalNonPayeOperations = sortedOperations
      .filter((op) => op.statut === "Non payé" && isBancaire(op))
      .reduce((sum, op) => sum + getEffectiveAmount(op, selectedAccountId), 0);

  // Factures Non Payées (modes bancaires uniquement)
  const totalNonPayeFactures = sortedFactures
      .filter((f) => f.statut === "Non payé" && Number(f.person_id) === selectedPersonId)
      .reduce((sum, f) => sum + getBancaireAmountFromFacture(f), 0);
      
  const totalNonPaye = totalNonPayeOperations - totalNonPayeFactures;

  // --- 5. TOTAL RESTANT (Total disponible + Total non payé) ---
  const totalRestant = totalDisponible + totalNonPaye;

  // Décomposition des catégories en listes visibles et masquées
  const visibleCategories = categories.filter(c => !c.is_hidden);
  const hiddenCategories = categories.filter(c => c.is_hidden);
  
  // État pour gérer l'ouverture de la corbeille
  const [isCorbeilleOpen, setIsCorbeilleOpen] = useState(false);

  // Pour mettre à jour la ref après chaque rendu
  React.useEffect(() => {
      if (selectAllCheckboxRef.current) {
          selectAllCheckboxRef.current.indeterminate = 
            selectedFactureIds.length > 0 && 
            selectedFactureIds.length < nonPayeesFiltered.length;
        }
  }, [selectedFactureIds, nonPayeesFiltered.length]);

  // Création des données pour les tableaux croisés et les graphiques
  const aggregateByCategoryAndMonth = (data, categoriesList, selectedYear) => {
    const aggregatedData = {};
    const monthlyTotals = {}; // ⭐ Nouvel objet pour stocker les totaux verticaux
    const currentYear = Number(selectedYear);
    const MONTHS_IN_YEAR = 12;
    const monthNames = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

    // Initialisation des totaux mensuels à 0
    monthNames.forEach(m => { monthlyTotals[m] = 0; });
    let grandTotal = 0;

    const filteredData = data.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate.getUTCFullYear() === currentYear; 
    });

    filteredData.forEach((item) => {
        const category = categoriesList.find((cat) => cat.id === Number(item.categorie_id));
        const categoryName = category?.name || "Inconnu";
        
        let amount = 0;
        if (item.sourceAccountId && item.destinationAccountId) {
            amount = getEffectiveAmount(item, selectedAccount?.id);
        } else {
            // 💡 TA FORMULE : Montant brut - Remboursements créances
            amount = Number(item.montant || 0) - Number(item.total_received || 0);
        }
        
        if (!aggregatedData[categoryName]) {
            aggregatedData[categoryName] = { monthly: {}, total: 0, id: category?.id || null };
        }
        
        const date = new Date(item.date);
        const month = date.getUTCMonth() + 1;
        const monthName = monthNames[month - 1];
        
        aggregatedData[categoryName].monthly[month] = (aggregatedData[categoryName].monthly[month] || 0) + amount;
        aggregatedData[categoryName].total += amount;

        // ⭐ Accumulation pour la ligne de total horizontal
        monthlyTotals[monthName] += amount;
        grandTotal += amount;
    });

    const tableData = Object.keys(aggregatedData)
        .map(catName => {
            const row = { name: catName, total: aggregatedData[catName].total };
            for (let i = 1; i <= MONTHS_IN_YEAR; i++) {
                row[monthNames[i - 1]] = aggregatedData[catName].monthly[i] || 0;
            }
            return row;
        })
        .sort((a, b) => b.total - a.total);
        
    return { 
        tableData, 
        monthNames, 
        monthlyTotals, // ⭐ On renvoie les totaux par mois
        grandTotal     // ⭐ On renvoie le total général
    };
  };
  
  // Les tableaux croisés utilisent les données NON FILTRÉES de l'année
  const { 
    tableData: aggregatedFactures, 
    monthNames: monthNamesFactures, 
    monthlyTotals: monthlyTotalsFactures, 
    grandTotal: grandTotalFactures 
  } = aggregateByCategoryAndMonth(sortedFactures, categories, selectedYear);

  const { 
    tableData: aggregatedOperations, 
    monthNames: monthNamesOperations, 
    monthlyTotals: monthlyTotalsOperations, 
    grandTotal: grandTotalOperations 
  } = aggregateByCategoryAndMonth(sortedOperations, categories, selectedYear);
  
  const aggregateExpensesByMonthForChart = (data, selectedYear) => {
      const monthlyData = {};
      const currentYear = Number(selectedYear);
      // ✅ Ordre Chronologique des Mois
      const monthLabels = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

      monthLabels.forEach(name => { monthlyData[name] = 0; });

      data.forEach((item) => {
          const date = new Date(item.date);
          const year = date.getUTCFullYear();
          
          if (year === currentYear) {
              const monthIndex = date.getUTCMonth(); // 0-11
              const monthName = monthLabels[monthIndex];
              const amount = Number(item.montant || 0);

              if (monthlyData.hasOwnProperty(monthName)) {
                  monthlyData[monthName] += amount;
              }
          }
      });

      return monthLabels.map(name => ({ name: name, total: monthlyData[name], }));
  };
  
  const chartData = aggregateExpensesByMonthForChart(filteredFactures, selectedYear);


  const handleDeleteFacture = async (factureId) => {
      try {
          // 🔑 CORRECTION 7 : DELETE /factures/:id
          await authFetch.delete(`/factures/${factureId}`);
          refreshDashboardData(); // Rafraîchit les données après la suppression
      } catch (error) {
          console.error(
              `❌ Erreur lors de la suppression de la facture ${factureId} :`,
              error
          );
          alert("Erreur lors de la suppression de la facture.");
      }
  };
  
  // Fonctions pour ouvrir/fermer les modales
  const handleEditFacture = (facture) => {
    setEditingFacture(facture);
    setShowFactureModal(true);
  };

  const handleCloseFactureModal = async () => {
    setShowFactureModal(false);
    setEditingFacture(null);
    await runRecurrenceCheck();
  };

  const handleEditOperation = (operation = null) => {
    setEditingOperation(operation);
    setShowOperationModal(true);
  };

  const handleCloseOperationModal = async () => {
    setShowOperationModal(false);
    setEditingOperation(null);
    await runRecurrenceCheck();
  };
  
  const getAccountName = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (!account) return "N/A";
    const person = persons.find(p => p.id === account.personId);
    return `${person?.name || "N/A"} - ${account.name}`;
  };

  // ✅ Gérer l'édition/ajout de catégorie
  const handleCategoryEdit = (category = null) => {
      if (category) {
          setEditingCategory(category);
          setCategoryName(category.name);
          // 💡 MISE À JOUR : Définir le type existant ou 'D' par défaut
          setCategoryType(category.type || 'D'); 
      } else {
          setEditingCategory(null);
          setCategoryName('');
          // 💡 MISE À JOUR : Type par défaut pour la création
          setCategoryType('D'); 
      }
      setIsCategoryFormVisible(true);
  };

  // ✅ Soumettre le formulaire d'ajout/modification de catégorie
  const handleCategorySubmit = async (e) => {
        e.preventDefault();

        const isEditing = !!editingCategory;
        
        // 💡 Validation du changement de type en mode édition
        if (isEditing && editingCategory.type !== categoryType) {
            // On appelle notre fonction de validation
            const success = await handleUpdateCategoryType(editingCategory, categoryType);
            if (!success) return; // On arrête tout si la validation échoue
        }

        const method = isEditing ? 'put' : 'post';
        const urlPath = isEditing ? `/categories/${editingCategory.id}` : `/categories`;

        const dataToSend = {
            name: categoryName,
            type: categoryType,
            is_hidden: isEditing ? (editingCategory.is_hidden || false) : false,  
        };

        try {
            const response = await authFetch[method](urlPath, dataToSend);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Échec de l'enregistrement.");
            }

            setEditingCategory(null);
            setIsCategoryFormVisible(false);
            setCategoryName('');
            setCategoryType('D');
            refreshAllData();
            notify(`Catégorie ${isEditing ? 'modifiée' : 'ajoutée'} avec succès !`);
        } catch (error) {
            notify(error.message, "error");
        }
    };

    // Logique de validation du changement de type
    const handleUpdateCategoryType = async (category, newType) => {
        if (category.type === newType) return false;

        try {
            // 1. Récupération du diagnostic d'usage
            const response = await authFetch.get(`/categories/usage-details/${category.id}`);
            const usage = await response.json();

            // 2. Application de la matrice de blocage avec messages personnalisés
            
            // Blocage vers DÉPENSES (D)
            if (newType === 'D' && usage.hasOperations) {
                notify("Action impossible : Cette catégorie est déjà utilisée pour des opérations (recettes). Vous ne pouvez pas la restreindre aux dépenses pures.", "error");
                return false;
            }

            // Blocage vers RECETTES (R)
            if (newType === 'R' && (usage.hasFactures || usage.hasRecurrences)) {
                notify("Action impossible : Cette catégorie est déjà utilisée pour des factures ou récurrences (dépenses). Vous ne pouvez pas la restreindre aux recettes pures.", "error");
                return false;
            }

            // Cas Spécifique : NEUTRE (N) -> On autorise généralement, mais on peut prévenir
            // Ici, selon ta matrice, le passage vers Neutre est toujours possible.

            // 3. Validation de l'utilisateur si on passe de "Les Deux" vers un type restreint alors qu'il y a de l'usage compatible
            // (Ex: Catégorie 'B' utilisée en facture, je veux la passer en 'D'. C'est valide mais sensible).
            
            // --- SI TOUT EST OK : APPEL API ---
            const dataToSend = { 
                name: category.name, 
                type: newType, 
                is_hidden: category.is_hidden 
            };
            
            const res = await authFetch.put(`/categories/${category.id}`, dataToSend);
            if (!res.ok) throw new Error("Erreur lors de la mise à jour");

            refreshAllData();
            notify(`Catégorie "${category.name}" mise à jour avec succès.`);
            return true;

        } catch (error) {
            notify(error.message, "error");
            return false;
        }
    };

  // ✅ Fonction pour basculer le statut is_hidden d'une catégorie
  const handleToggleCategoryVisibility = async (category) => {
      if (!category) return;

      const newIsHidden = !category.is_hidden;
      
  	  // Ajout de la confirmation avant l'action
        const actionLabel = newIsHidden ? 'masquer' : 'rétablir';
        if (!window.confirm(`Confirmez-vous de ${actionLabel} la catégorie "${category.name}" ?`)) {
            return;
        }

        const dataToSend = {
            name: category.name,
            is_hidden: newIsHidden,
            type: category.type || 'D', // Le type est aussi requis par l'API
        };

        try {
            const response = await authFetch.put(`/categories/${category.id}`, dataToSend);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Échec de la mise à jour de la visibilité.`);
            }

            refreshAllData();  

            // 💡 UTILISATION DE NOTIFY POUR LE SUCCÈS
            const statusMsg = newIsHidden ? 'masquée (dans la corbeille)' : 'rétablie et visible';
            notify(`Catégorie "${category.name}" est maintenant ${statusMsg}.`, 'success');

        } catch (error) {
            console.error(`❌ Erreur de bascule de visibilité :`, error);
            // 💡 UTILISATION DE NOTIFY POUR L'ERREUR
            notify(`Échec de la modification : ${error.message}`, 'error');
        }
    };

    // ✅ Gérer l'édition/ajout de personne
  const handlePersonEdit = (person = null) => {
      if (person) {
          setEditingPerson(person);
          setPersonName(person.name);
          // Le champ est stocké en BOOLEAN/INT en DB, donc il doit être TRUE/FALSE
          setIsManaged(person.is_managed === 1 || person.is_managed === true); 
      } else {
          setEditingPerson(null);
          setPersonName('');
          setIsManaged(true); // Par défaut, une nouvelle personne est gérée
      }
      setIsPersonFormVisible(true);
  };

  // ✅ Soumettre le formulaire d'ajout/modification de personne
  const handlePersonSubmit = async (e, directPersonData = null) => {
      // Si l'événement est présent, c'est une soumission de formulaire (création/modification complète)
      if (e) {
          e.preventDefault();
      }
      
      // Déterminer les données à envoyer
      const dataToSend = directPersonData ? {
          name: directPersonData.name,
          is_managed: directPersonData.is_managed,
      } : {
          name: personName,
          is_managed: isManaged,
      };

      const isEditing = !!editingPerson || !!directPersonData;
      const targetPerson = directPersonData || editingPerson;

      const method = isEditing ? 'put' : 'post';
  	  const urlPath = isEditing
          ? `/persons/${targetPerson.id}` // <- Chemin relatif
          : `/persons`; // <- Chemin relatif

      try {
          // 🔑 CORRECTION 10 : POST/PUT /persons
          const response = await authFetch[method](urlPath, dataToSend);

          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || `Échec de ${isEditing ? 'la modification' : 'l\'ajout'} de personne.`);
          }

          // Réinitialiser le formulaire SEULEMENT si c'était le formulaire de modification/création
          if (!directPersonData) {
              setEditingPerson(null);
              setIsPersonFormVisible(false);
              setPersonName('');
              setIsManaged(true);
          }
          
          if (refreshAllData) {
              refreshAllData(); 
          }

          notify(`Personne ${isEditing ? 'modifiée' : 'ajoutée'} avec succès !`);

      } catch (error) {
          console.error('❌ Erreur de soumission de personne:', error);
          notify(`Erreur : ${error.message}`);
      }
  };

    // ===============================================
    // ✅ GESTION DES ACTIONS GROUPÉES (P5/P7)
    // ===============================================
    
    // Fonction utilitaire pour la sélection (pour Factures)
    const handleSelectFacture = (factureId) => {
        setSelectedFactureIds(prev => 
            prev.includes(factureId)
                ? prev.filter(id => id !== factureId)
                : [...prev, factureId]
        );
    };

    // Fonction utilitaire pour la sélection (pour Opérations)
    const handleSelectOperation = (operationId) => {
        setSelectedOperationIds(prev => 
            prev.includes(operationId)
                ? prev.filter(id => id !== operationId)
                : [...prev, operationId]
        );
    };

    // Hook pour mettre à jour la ref `indeterminate`
    const updateSelectAllRef = (ref, currentIds, filteredList) => {
        if (ref.current) {
            const allIds = filteredList.map(item => item.id);
            const isAllSelected = allIds.length > 0 && currentIds.length === allIds.length;
            const isIndeterminate = currentIds.length > 0 && currentIds.length < allIds.length;
            
            ref.current.checked = isAllSelected;
            // Utilise l'attribut DOM direct pour l'état indéterminé
            ref.current.indeterminate = isIndeterminate;
        }
    };
    
    // Mettre à jour l'état indéterminé des cases à cocher après chaque rendu
    useEffect(() => {
        // Pour les Factures
        updateSelectAllRef(selectAllFacturesRef, selectedFactureIds, filteredFactures);
        // Pour les Opérations
        updateSelectAllRef(selectAllOperationsRef, selectedOperationIds, filteredOperations);
    }, [selectedFactureIds, selectedOperationIds, filteredFactures, filteredOperations]);

    // Gère la sélection/désélection de TOUTES les factures AFFICHÉES
    const handleSelectAllFactures = (list) => {
        const allIds = list.map(f => f.id);
        const shouldSelectAll = selectedFactureIds.length < allIds.length;
        
        setSelectedFactureIds(shouldSelectAll ? allIds : []);
    };

    // Gère la sélection/désélection de TOUTES les opérations AFFICHÉES
    const handleSelectAllOperations = (list) => {
        const allIds = list.map(op => op.id);
        const shouldSelectAll = selectedOperationIds.length < allIds.length;
        
        setSelectedOperationIds(shouldSelectAll ? allIds : []);
    };

    // ✅ FONCTION : Suppression groupée de Factures/Opérations
    const handleBatchDelete = async (type) => {
        const idsToDelete = type === 'factures' ? selectedFactureIds : selectedOperationIds;
        
        if (idsToDelete.length === 0) {
            alert("Veuillez sélectionner au moins une transaction à supprimer.");
            return;
        }

        if (!window.confirm(`Confirmez-vous la suppression DÉFINITIVE de ${idsToDelete.length} ${type} ?`)) {
            return;
        }

        try {
          // 🔑 CORRECTION 11 : DELETE /transactions/batch-delete
            const response = await authFetch.post('/transactions/batch-delete', {
                type,
                ids: idsToDelete,
            });

            const data = await response.json();

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || "Échec de la suppression groupée.");
            }
            
            // Succès
            if (type === 'factures') setSelectedFactureIds([]); else setSelectedOperationIds([]);
            refreshDashboardData(); 
            notify(data.message || "Suppression réussie");

        } catch (error) {
            console.error(`❌ Erreur suppression groupée ${type}:`, error);
            alert(`Erreur lors de la suppression groupée : ${error.message}`);
        }
    };

    // ✅ FONCTION : Mise à jour de statut groupée (Utilisée par l'onglet Non Payées)
    const handleBatchStatusUpdate = async (newStatut) => {
        const validFactureIds = selectedFactureIds
            .map(id => Number(id))
            .filter(id => !isNaN(id) && id > 0);

        if (validFactureIds.length === 0) {
            alert("Veuillez sélectionner au moins une facture valide.");
            return;
        }

        if (!window.confirm(`Confirmez-vous le changement de statut de ${validFactureIds.length} facture(s) à "${newStatut}" ?`)) {
            return;
        }

        try {
          // 🔑 CORRECTION 12 : PUT /factures/batch-status
            const response = await authFetch.put('/factures/batch-status', {
                factureIds: validFactureIds, 
                newStatut 
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Échec de la mise à jour groupée.");
            }
            
            const successData = await response.json(); 

            setSelectedFactureIds([]); 
            refreshDashboardData(); 
            notify(successData.message);

        } catch (error) {
            console.error("❌ Erreur de mise à jour groupée :", error);
            notify(`Erreur : ${error.message}`);
        }
    };
    
    // Ouvre le modal de modification groupée
    const handleOpenBatchUpdate = (type) => {
        const ids = type === 'factures' ? selectedFactureIds : selectedOperationIds;
        if (ids.length === 0) {
            alert("Veuillez sélectionner au moins une transaction à modifier.");
            return;
        }
        setBatchType(type);
        setShowBatchModal(true);
    };

    const handleExportExcel = async () => {
        const dataToExport = [];
        const selectedPersonId = selectedPerson?.id;

        // --- 1. RÉCUPÉRATION DU SOLDE INITIAL RÉEL ---
        let realOpeningBalance = 0;
        try {
            const resp = await authFetch.get(`/accounts/closing-solde/${selectedAccount.id}/${selectedPersonId}/${selectedYear - 1}`);
            if (resp.ok) {
                const data = await resp.json();
                realOpeningBalance = data.solde;
            } else {
                realOpeningBalance = Number(selectedAccount?.soldeDebutAnnee || 0);
            }
        } catch (e) {
            realOpeningBalance = Number(selectedAccount?.soldeDebutAnnee || 0);
        }

        dataToExport.push({
            'Date': `01/01/${selectedYear}`,
            'Type': 'SOLDE INITIAL',
            'Catégorie': 'Contrôle',
            'Libellé / Description': `Solde réel au 01/01/${selectedYear}`,
            'Mode de Règlement': 'N/A',
            'Montant': realOpeningBalance,
            'Bancaire (Impact Solde)': 'OUI',
            'Payeur / Rembourseur': 'Système',
            'Lien Facture ID': '',
            'ID Flux': 'START'
        });

        // --- 2. OPÉRATIONS ---
        filteredOperations.forEach(op => {
            const amount = getEffectiveAmount(op, selectedAccount?.id);
            dataToExport.push({
                'Date': new Date(op.date).toLocaleDateString('fr-FR'),
                'Type': op.sourceAccountId && op.destinationAccountId ? 'Transfert' : 'Opération',
                'Catégorie': op.categoryName || 'N/A',
                'Libellé / Description': op.description,
                'Mode de Règlement': op.paiementMode || 'N/A',
                'Montant': amount,
                'Bancaire (Impact Solde)': isBancaire(op) ? 'OUI' : 'NON',
                'Payeur / Rembourseur': selectedPerson?.name,
                'Lien Facture ID': '',
                'ID Flux': `OP-${op.id}`
            });
        });

        // --- 3. FACTURES (Avec règle du payeur) ---
        filteredFactures.forEach(f => {
            const payments = f.bill_payments || [];
            payments.forEach(p => {
                const mode = p.mode || p.payment_mode;
                const isBancaireMode = !NON_BANCAIRE_MODES.includes(mode);
                const payerId = Number(p.payer_person_id);
                
                // ⭐ RÈGLE DU PAYEUR : 
                // L'impact est OUI seulement si le mode est bancaire ET que c'est la personne sélectionnée qui paye.
                const impactSolde = (isBancaireMode && payerId === selectedPersonId) ? 'OUI' : 'NON';

                dataToExport.push({
                    'Date': new Date(f.date).toLocaleDateString('fr-FR'),
                    'Type': 'Facture (Règlement)',
                    'Catégorie': categories.find(c => c.id === f.categorie_id)?.name || 'N/A',
                    'Libellé / Description': f.description,
                    'Mode de Règlement': mode,
                    'Montant': -Number(p.montant || p.amount_paid),
                    'Bancaire (Impact Solde)': impactSolde,
                    'Payeur / Rembourseur': persons.find(per => per.id === payerId)?.name || 'Inconnu',
                    'Lien Facture ID': f.id,
                    'ID Flux': `FAC-${f.id}`
                });
            });
        });

        // --- 4. REMBOURSEMENTS ---
        reimbursementFlow.forEach(reimb => {
            const mode = reimb.reimbursement_mode || reimb.mode;
            const isBancaireMode = !NON_BANCAIRE_MODES.includes(mode);
            const isSource = reimb.sourceAccountId === selectedAccount?.id;
            const isDest = reimb.destinationAccountId === selectedAccount?.id;
            
            const impactSurCeCompte = isBancaireMode ? (isSource || isDest) : (reimb.facture_account_id === selectedAccount?.id);

            if (impactSurCeCompte) {
                const amount = Number(reimb.reimbursement_amount);
                const isPayer = Number(reimb.debtor_person_id) === selectedPersonId;

                dataToExport.push({
                    'Date': new Date(reimb.reimbursement_date || reimb.date).toLocaleDateString('fr-FR'),
                    'Type': 'Remboursement',
                    'Catégorie': 'Dettes / Créances',
                    'Libellé / Description': `Remb. ${isPayer ? 'versé à' : 'reçu de'} ${reimb.partner_name || 'tiers'}`,
                    'Mode de Règlement': mode,
                    'Montant': isPayer ? -amount : amount,
                    'Bancaire (Impact Solde)': isBancaireMode ? 'OUI' : 'NON',
                    'Payeur / Rembourseur': persons.find(per => per.id === Number(reimb.debtor_person_id))?.name || 'N/A',
                    'Lien Facture ID': reimb.facture_id, // ⭐ Ajout du N° de facture
                    'ID Flux': `REIMB-${reimb.id || 'N/A'}`
                });
            }
        });

        // --- 5. GÉNÉRATION ---
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Contrôle");
        
        // Ajustement des colonnes pour la lisibilité
        worksheet['!cols'] = [
            {wch: 12}, {wch: 18}, {wch: 20}, {wch: 35}, {wch: 20}, {wch: 12}, {wch: 15}, {wch: 20}, {wch: 15}, {wch: 15}
        ];

        XLSX.writeFile(workbook, `Export_${selectedAccount?.name}_${selectedYear}.xlsx`);
        notify("Fichier d'export généré", "success");
    };

    // ===============================================
    // ✅ MODAL DE MISE À JOUR GROUPÉE SIMPLE
    // ===============================================
    const BatchUpdateForm = ({ type, onClose }) => {
        const [updates, setUpdates] = useState({});
        const isFacture = type === 'factures';
        const ids = isFacture ? selectedFactureIds : selectedOperationIds;
        const transactionTypeLabel = isFacture ? "Factures" : "Opérations";

        const handleUpdateChange = (e) => {
            const { name, value } = e.target;
            setUpdates(prev => {
                const newUpdates = { ...prev };
                if (value === '' || value === null) {
                    delete newUpdates[name]; // Ne pas envoyer de champ vide
                } else {
                    newUpdates[name] = value;
                }
                return newUpdates;
            });
        };
        
        const handleSubmit = async (e) => {
            e.preventDefault();
            if (Object.keys(updates).length === 0) {
                alert("Aucun champ n'a été modifié.");
                return;
            }

            try {
                const response = await authFetch.put('/transactions/batch-update', {
                    type: type, 
                    ids: ids, 
                    updates: updates 
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Échec de la mise à jour groupée.");
                }
                
                // Succès
                if (isFacture) setSelectedFactureIds([]); else setSelectedOperationIds([]);
                refreshDashboardData(); 
                onClose(); 
                notify(`Mise à jour groupée réussie pour ${ids.length} ${transactionTypeLabel}.`);
            } catch (error) {
                console.error("❌ Erreur de soumission groupée :", error);
                notify(`Erreur lors de la mise à jour groupée : ${error.message}`);
            }
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={{ ...styles.modalContent, backgroundColor: themeColors.mainBackground }}>
                    <h3 style={{ color: themeColors.text }}>Modification de {ids.length} {transactionTypeLabel}</h3>
                    <p style={{ 
                        ...styles.modalWarning,
                        backgroundColor: themeColors.corbeilleBG, // Fond avertissement dynamique
                        color: themeColors.corbeilleText // Texte avertissement dynamique
                    }}>
                        Seuls les champs remplis ci-dessous seront modifiés.
                    </p>
                    <form onSubmit={handleSubmit} style={styles.form}>

                        {/* Champ : Date */}
                        <label style={{ color: themeColors.text }}>Date :</label>
                        <input
                            type="date"
                            name="date"
                            onChange={handleUpdateChange}
                            style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground }}
                        />
                        
                        {/* Champ : Catégorie */}
                        <label style={{ color: themeColors.text }}>Catégorie :</label>
                        <select name="categorie_id" onChange={handleUpdateChange} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground, color: themeColors.text }}>
                            <option value="">-- Ne pas modifier --</option>
                            {visibleCategories.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>

                        {/* Champ : Statut */}
                        <label style={{ color: themeColors.text }}>Statut :</label>
                        <select name="statut" onChange={handleUpdateChange} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground, color: themeColors.text }}>
                            <option value="">-- Ne pas modifier --</option>
                            <option value="Payé">Payé</option>
                            <option value="Non payé">Non payé</option>
                        </select>
                        
                        {/* Champ : Description */}
                        <label style={{ color: themeColors.text }}>Description :</label>
                        <input
                            type="text"
                            placeholder="Nouvelle description (laisser vide pour ne pas modifier)"
                            name="description"
                            onChange={handleUpdateChange}
                            style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground, color: themeColors.text }}
                        />

                        {/* Champ : Montant (pourrait être dangereux, mais nous le laissons pour la flexibilité) */}
                        <label style={{ color: themeColors.text }}>Montant :</label>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Nouveau montant (laisser vide pour ne pas modifier)"
                            name="montant"
                            onChange={handleUpdateChange}
                            style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground, color: themeColors.text }}
                        />

                        {/* Champs spécifiques aux Opérations */}
                        {!isFacture && (
                            <>
                                <label style={{ color: themeColors.text }}>Mode de paiement :</label>
                                <select name="paiementMode" onChange={handleUpdateChange} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground, color: themeColors.text }}>
                                    <option value="">-- Ne pas modifier --</option>
                                    {/* Modes de paiement... */}
                                    {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
                                </select>
                            </>
                        )}
                        
                        {/* 💡 NOTE: D'autres champs (date, compte) peuvent être ajoutés ici si nécessaire */}

                        <div style={styles.modalActions}>
                            <button 
                                type="button" 
                                style={{ ...styles.cancelButton, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }} 
                                onClick={onClose}>
                                Annuler
                            </button>
                            <button 
                                type="submit" 
                                style={{ ...styles.saveButton, backgroundColor: themeColors.success, color: themeColors.textSecondary }} 
                                disabled={Object.keys(updates).length === 0}>
                                Appliquer les changements ({Object.keys(updates).length})
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

  // Définition de la variable pour le contenu du footer (Totaux ou Corbeille)
  const footerContent = selectedTab === "gerer-categories" ? (
      // 🎯 Contenu spécial de la Corbeille
      <>
          {/* L'en-tête de la Corbeille */}
          <div 
              onClick={() => setIsCorbeilleOpen(prev => !prev)} 
              style={{ 
                  ...styles.corbeilleHeader, 
                  cursor: 'pointer',
                  backgroundColor: themeColors.corbeilleBG, // Fond dynamique
                  color: themeColors.corbeilleText, // Texte dynamique
                  border: `1px solid ${themeColors.corbeilleText}30`, // Bordure dynamique
              }}
          >
              <h3>
                  Corbeille (Masquées : {hiddenCategories.length}) 
                  <span style={{ marginLeft: '10px' }}>{isCorbeilleOpen ? '▼' : '▶'}</span>
              </h3>
          </div>

          {/* Contenu Déroulant de la Corbeille */}
          {isCorbeilleOpen && (
              <div style={{ 
                  ...styles.corbeilleContent, 
                  backgroundColor: themeColors.background, // Fond dynamique
                  border: `1px solid ${themeColors.corbeilleText}30`, // Bordure dynamique
                  borderTop: 'none',
              }}>
                  <p style={{marginBottom: '10px', color: themeColors.text}}>Catégories masquées (Cliquez sur "Rétablir" pour les rendre visibles) :</p>
                  <table style={styles.table}>
                      <thead>
                          <tr>
                              <th style={styles.left}>Nom</th>
                              <th style={styles.center}>Actions</th>
                          </tr>
                      </thead>
                      <tbody>
                          {hiddenCategories.map((c) => (
                                <CategoryRow 
                                    key={c.id} 
                                    category={c}
                                    handleCategoryEdit={handleCategoryEdit}
                                    handleToggleCategoryVisibility={handleToggleCategoryVisibility}
                                    refreshAllData={refreshAllData}
                                    styles={styles}
                                    themeColors={themeColors} // Passage des couleurs
                                />
                          ))}
                    </tbody>
                  </table>
              </div>
          )}
      </>
  ) : (
      // 🎯 Contenu des Totaux par Défaut
      <>
          <div style={styles.totalsRow}>
              <div>
              <strong>Total banque</strong>
            </div>
            <div>
              <strong>Total factures</strong>
            </div>
            <div>
              <strong>Total disponible</strong>
            </div>
            <div>
              <strong>Non payés</strong>
            </div>
            <div>
              <strong>Total restant</strong>
            </div>
          </div>
          <div style={styles.totalsRow}>
            <div>
              <strong>{formatCurrency(totalBanque)} €</strong>
            </div>
            <div>
              <strong>{formatCurrency(totalFactures)} €</strong>
            </div>
            <div>
              <strong>
                  <span style={{ color: totalDisponible >= 0 ? themeColors.success : themeColors.danger }}>
                    {formatCurrency(totalDisponible)} €
                  </span>
              </strong>
            </div>
            <div>
              <strong>{formatCurrency(totalNonPaye)} €</strong>
            </div>
            <div>
              <strong>
                  <span style={{ color: totalRestant >= 0 ? themeColors.success : themeColors.danger }}>
                    {formatCurrency(totalRestant)} €
                  </span>
              </strong>
            </div>
          </div>
      </>
  );

  return (
    <div style={styles.container}>
      <div style={{ ...styles.sidebar, background: themeColors.backgroundnav }}> {/* Fond Sidebar dynamique */}
        <div style={{ ...styles.creationButtonsContainer, borderBottom: `1px solid ${themeColors.primary}50` }}>
            <button
                style={{ ...styles.sidebarButton, backgroundColor: themeColors.primary, color: themeColors.textSecondary }}
                onClick={() => handleEditFacture(null)}
            >
                ➕ Nouvelle facture
            </button>
            <button
                style={{ ...styles.sidebarButton, backgroundColor: themeColors.primary, color: themeColors.textSecondary }}
                onClick={() => handleEditOperation(null)}
            >
                ➕ Nouvelle opération
            </button>
            <button 
                style={{ ...styles.sidebarButton, backgroundColor: themeColors.primary, color: themeColors.textSecondary }}
                onClick={openNewRecurrence}
            >
                ➕ Nouvelle récurrence
            </button>
            <button 
                style={{ 
                    ...styles.sidebarButton, 
                    backgroundColor: themeColors.success, 
                    color: themeColors.textSecondary,
                    marginTop: '10px'
                }}
                onClick={handleExportExcel}
            >
                📥 Exporter Excel (.xlsx)
            </button>
        </div>
        <h3 style={{ color: themeColors.secondary }}>Navigation</h3>
        <ul style={styles.navList}>
          {/* Liens de navigation */}
          {[
            "factures", "non-payees", "cat-factures", "banque", "cat-banque", "gerer-personnes", "gerer-categories", "graphique",
          ].map((tab) => (
            <li
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                ...styles.navItem,
                // Couleurs dynamiques
                backgroundColor: selectedTab === tab ? themeColors.navActive : "transparent",
                color: selectedTab === tab ? themeColors.text : themeColors.text,
                fontWeight: selectedTab === tab ? "700" : "550",
              }}
            >
              {tab === "factures" && "Factures"}
              {tab === "non-payees" && "Non payées"}
              {tab === "cat-factures" && "Catégorie (Factures)"}
              {tab === "banque" && "Banque"}
              {tab === "cat-banque" && "Catégorie (Banque)"}
              {tab === "gerer-personnes" && "Gérer Personnes"}
              {tab === "gerer-categories" && "Gérer Catégories"}
              {tab === "graphique" && "Graphique"}
            </li>
          ))}
          <Link to="/situation" style={styles.navItem}>
            Situation
          </Link>
          <Link to="/recurrence" style={styles.navItem}>
            Récurrence
          </Link>
        </ul>
      </div>

      <div style={{ ...styles.main, background: themeColors.mainBackground, border: `1px solid ${themeColors.inputBorder}` }}> {/* Fond Main dynamique */}
        {/* 💡 BANDEAU D'ALERTE D'ABONNEMENT */}
        {(isTrial || isExpired) && (
            <div style={{
                padding: '10px 15px',
                marginBottom: '15px',
                borderRadius: '5px',
                textAlign: 'center',
                fontWeight: 'bold',
                // Couleur conditionnelle
                backgroundColor: isExpired ? themeColors.danger + '30' : themeColors.warning + '30',
                color: isExpired ? themeColors.danger : themeColors.warning,
                border: `1px solid ${isExpired ? themeColors.danger : themeColors.warning}`,
            }}>
                {isExpired 
                    ? `🚨 Votre plan ${plan} a expiré. Veuillez choisir un abonnement.`
                    : `⏳ Vous êtes en essai gratuit (${plan}). Il vous reste ${trialDaysRemaining} jour(s).`
                }
            </div>
        )}
        {/* FIN BANDEAU */}

        <div style={styles.content}>
          {/* Rendu des onglets */}
          
          {selectedTab === "factures" && (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
                  <h2 style={{ color: themeColors.secondary, margin: '0 0 10px 0' }}>Liste des factures</h2>

                  {/* 💡 BOUTON DE TOGGLE DE FILTRE */}
                    <button 
                        onClick={() => setIsFilterBarVisible(p => !p)} 
                        style={{
                            ...styles.topButton, 
                            marginBottom: '15px', 
                            width: '250px',
                            backgroundColor: areFactureFiltersActive && !isFilterBarVisible ? themeColors.accent : themeColors.primary,
                            color: areFactureFiltersActive && !isFilterBarVisible ? themeColors.text : themeColors.textSecondary,
                        }}
                    >
                        {isFilterBarVisible 
                          ? 'Masquer les filtres' 
                          : areFactureFiltersActive 
                                ? 'Filtres actifs 💡 (Afficher)' 
                                : 'Afficher les filtres'
                        }
                    </button>

                    {/* 💡 RENDU CONDITIONNEL DE LA BARRE DE FILTRE */}
                    {isFilterBarVisible && (
                        <div style={{ flexShrink: 0 }}>
                            <FilterBar 
                                filter={factureFilter} 
                                setFilter={setFactureFilter} 
                                type="facture" 
                                categories={visibleCategories} 
                                styles={styles}
                                filteredCount={filteredFactures.length}
                                totalCount={sortedFactures.length}
                                themeColors={themeColors} // Passage des couleurs
                            />
                        </div>
                    )}
                  
                  {/* ✅ ACTIONS GROUPÉES FACTURES */}
                  {selectedFactureIds.length > 0 && (
                    <div style={{...styles.batchActionsBar, flexShrink: 0}}>
                          <button
                              style={{ ...styles.topButton, backgroundColor: themeColors.actionBlue, color: themeColors.textSecondary }}
                              onClick={() => handleOpenBatchUpdate('factures')}
                              disabled={selectedFactureIds.length === 0}
                          >
                              Modifier la sélection ({selectedFactureIds.length})
                          </button>
                          <button
                              style={{ ...styles.topButton, backgroundColor: themeColors.danger, color: themeColors.textSecondary }}
                              onClick={() => handleBatchDelete('factures')}
                              disabled={selectedFactureIds.length === 0}
                          >
                              Supprimer la sélection ({selectedFactureIds.length})
                          </button>
                          <button
                              style={{ ...styles.cancelButton, padding: '10px 15px', marginTop: 0, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                              onClick={() => setSelectedFactureIds([])}
                              disabled={selectedFactureIds.length === 0}
                          >
                              Annuler la sélection ({selectedFactureIds.length})
                          </button>
                    </div>
                  )}
                  <div style={{ ...styles.scrollContainer, border: `1px solid ${themeColors.inputBorder}` }}>
                    <table style={{ ...styles.table, backgroundColor: themeColors.mainBackground }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ backgroundColor: themeColors.background }}>
                                {/* ✅ COLONNE CHECKBOX */}
                                <th style={{...styles.center, width: '40px', borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                                    <input
                                        type="checkbox"
                                        ref={selectAllFacturesRef}
                                        onChange={() => handleSelectAllFactures(filteredFactures)}
                                        disabled={filteredFactures.length === 0}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}></th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Date</th>
                                <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Libellé</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Montant</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Statut</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody >
                            {filteredFactures.map((f) => (
                                <TransactionRow
                                    key={f.id}
                                    rowStyle={selectedFactureIds.includes(f.id) ? {backgroundColor: themeColors.navActive} : {}}
                                    checkbox={
                                        <input
                                            type="checkbox"
                                            checked={selectedFactureIds.includes(f.id)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    }
                                    facture={f}
                                    categories={categories}
                                    allPersons={persons}
                                    selectedFactureIds={selectedFactureIds}
                                    handleSelectFacture={handleSelectFacture}
                                    handleEditFacture={handleEditFacture} 
                                    handleDeleteFacture={handleDeleteFacture}
                                    isExpanded={expandedFactureId === f.id}
                                    onToggleExpand={handleToggleFactureExpand}
                                    themeColors={themeColors} // Passage des couleurs
                                />
                            ))}
                        </tbody>
                    </table>
                  </div>
              </div>
          )}

          {selectedTab === "non-payees" && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <h2 style={{ color: themeColors.secondary, margin: '0 0 10px 0' }}>Factures non payées</h2>
                
                {/* Barre de filtre */}
                <button 
                    onClick={() => setIsFilterBarVisible(p => !p)} 
                    style={{
                        ...styles.topButton, 
                        marginBottom: '15px', 
                        width: '250px',
                        flexShrink: 0, // Empêche le bouton de rétrécir
                        backgroundColor: areNonPayeesFiltersActive && !isFilterBarVisible ? themeColors.accent : themeColors.primary,
                        color: areNonPayeesFiltersActive && !isFilterBarVisible ? themeColors.text : themeColors.textSecondary,
                    }}
                >
                    {isFilterBarVisible ? 'Masquer les filtres' : areNonPayeesFiltersActive ? 'Filtres actifs 💡 (Afficher)' : 'Afficher les filtres'}
                </button>

                {isFilterBarVisible && (
                    <div style={{ flexShrink: 0 }}>
                        <FilterBar 
                            filter={factureFilter} 
                            setFilter={setFactureFilter} 
                            type="facture" 
                            categories={visibleCategories} 
                            styles={styles}
                            filteredCount={filteredFactures.length}
                            totalCount={sortedFactures.length}
                            themeColors={themeColors}
                        />
                    </div>
                )}
                
                {/* Actions groupées */}
                {selectedFactureIds.length > 0 && (
                    <div style={{ ...styles.batchActionsBar, flexShrink: 0 }}>
                        <button
                            style={{ ...styles.topButton, backgroundColor: themeColors.success, color: themeColors.textSecondary }}
                            onClick={() => handleBatchStatusUpdate("Payé")}
                        >
                            Marquer {selectedFactureIds.length} comme Payée(s)
                        </button>
                        <button
                            style={{ ...styles.topButton, backgroundColor: themeColors.actionBlue, color: themeColors.textSecondary }}
                            onClick={() => handleOpenBatchUpdate('factures')}
                        >
                            Modifier la sélection ({selectedFactureIds.length})
                        </button>
                        <button
                            style={{ ...styles.topButton, backgroundColor: themeColors.danger, color: themeColors.textSecondary }}
                            onClick={() => handleBatchDelete('factures')}
                        >
                            Supprimer la sélection ({selectedFactureIds.length})
                        </button>
                        <button
                            style={{ ...styles.cancelButton, padding: '10px 15px', marginTop: 0, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                            onClick={() => setSelectedFactureIds([])}
                        >
                            Annuler
                        </button>
                    </div>
                )}

                {/* 💡 Zone de défilement flexible */}
                <div style={styles.scrollContainer}>
                    <table style={{ ...styles.table, backgroundColor: themeColors.mainBackground, marginTop: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ backgroundColor: themeColors.background }}>
                                <th style={{...styles.center, width: '40px', borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                                    <input
                                        type="checkbox"
                                        checked={selectedFactureIds.length > 0 && selectedFactureIds.length === nonPayeesFiltered.length}
                                        ref={selectAllCheckboxRef}
                                        onChange={() => handleSelectAllFactures(nonPayeesFiltered)}
                                        disabled={nonPayeesFiltered.length === 0}
                                    />
                                </th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Date</th>
                                <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Libellé</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Montant</th>
                            </tr>
                        </thead>
                        <tbody>
                            {nonPayeesFiltered.map((f) => (
                                <tr
                                    key={f.id}
                                    style={{ 
                                        cursor: "pointer", 
                                        backgroundColor: selectedFactureIds.includes(f.id) ? themeColors.navActive : themeColors.mainBackground 
                                    }}
                                    onClick={() => handleEditFacture(f)} 
                                >
                                    <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }} onClick={(e) => { e.stopPropagation(); handleSelectFacture(f.id); }}>
                                        <input type="checkbox" checked={selectedFactureIds.includes(f.id)} readOnly />
                                    </td>
                                    <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>
                                        {new Date(f.date).toLocaleDateString("fr-FR", { timeZone: "UTC" })}
                                    </td>
                                    <td style={{ ...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}` }}>
                                        {categories.find((c) => c.id === f.categorie_id)?.name || "N/A"} - {f.description}
                                    </td>
                                    <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>
                                        {formatCurrency(f.montant)} €
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
          )}

          {selectedTab === "cat-factures" && (
                <>
                    <h2 style={{ color: themeColors.secondary, marginBottom: '5px' }}>Répartition des factures ({selectedYear})</h2>
                    <div style={styles.scrollContainer}>
                        <table style={{ ...styles.tableCompact, backgroundColor: themeColors.mainBackground }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <tr style={{ backgroundColor: themeColors.background }}>
                                    <th style={{...styles.left, ...styles.headerCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Catégorie</th>
                                    {monthNamesFactures.map(month => (
                                        <th key={month} style={{...styles.center, ...styles.headerCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>{month}</th>
                                    ))}
                                    <th style={{...styles.center, ...styles.headerCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aggregatedFactures.map((row, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? themeColors.mainBackground : themeColors.formBackground }}>
                                        <td style={{...styles.left, ...styles.cellCompact, borderBottomColor: themeColors.inputBorder}}>{row.name}</td>
                                        {monthNamesFactures.map(month => (
                                            <td key={month} style={{...styles.center, ...styles.cellCompact, borderBottomColor: themeColors.inputBorder}}>
                                                {formatCurrency(row[month])} {row[month] !== 0 && '€'}
                                            </td>
                                        ))}
                                        <td style={{...styles.center, ...styles.cellCompact, fontWeight: 'bold', borderBottomColor: themeColors.inputBorder}}>
                                            {formatCurrency(row.total)} {row.total !== 0 && '€'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: themeColors.navActive, fontWeight: 'bold' }}>
                                    <td style={{...styles.left, ...styles.cellCompact}}>Total</td>
                                    {monthNamesFactures.map(month => (
                                        <td key={month} style={{...styles.center, ...styles.cellCompact}}>
                                            {formatCurrency(monthlyTotalsFactures[month])} {monthlyTotalsFactures[month] !== 0 && '€'}
                                        </td>
                                    ))}
                                    <td style={{...styles.center, ...styles.cellCompact}}>{formatCurrency(grandTotalFactures)} €</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </>
            )}

           {selectedTab === "banque" && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <h2 style={{ color: themeColors.secondary, margin: '0 0 10px 0' }}>Liste des opérations bancaires</h2>
                
                <button 
                    onClick={() => setIsFilterBarVisible(p => !p)} 
                    style={{
                        ...styles.topButton, 
                        marginBottom: '15px', 
                        width: '250px',
                        flexShrink: 0,
                        backgroundColor: areOperationFiltersActive && !isFilterBarVisible ? themeColors.accent : themeColors.primary,
                        color: areOperationFiltersActive && !isFilterBarVisible ? themeColors.text : themeColors.textSecondary,
                    }}
                >
                    {isFilterBarVisible ? 'Masquer les filtres' : areOperationFiltersActive ? 'Filtres actifs 💡 (Afficher)' : 'Afficher les filtres'}
                </button>

                {isFilterBarVisible && (
                    <div style={{ flexShrink: 0 }}>
                        <FilterBar 
                            filter={operationFilter}
                            setFilter={setOperationFilter}
                            type="operation"
                            categories={visibleCategories}
                            styles={styles}
                            filteredCount={filteredOperations.length}
                            totalCount={sortedOperations.length}
                            themeColors={themeColors}
                        />
                    </div>
                )}

                {selectedOperationIds.length > 0 && (
                    <div style={{ ...styles.batchActionsBar, flexShrink: 0 }}>
                        <button
                            style={{ ...styles.topButton, backgroundColor: themeColors.actionBlue, color: themeColors.textSecondary }}
                            onClick={() => handleOpenBatchUpdate('operations')}
                        >
                            Modifier la sélection ({selectedOperationIds.length})
                        </button>
                        <button
                            style={{ ...styles.topButton, backgroundColor: themeColors.danger, color: themeColors.textSecondary }}
                            onClick={() => handleBatchDelete('operations')}
                        >
                            Supprimer la sélection ({selectedOperationIds.length})
                        </button>
                        <button
                            style={{ ...styles.cancelButton, padding: '10px 15px', marginTop: 0, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                            onClick={() => setSelectedOperationIds([])}
                        >
                            Annuler
                        </button>
                    </div>
                )}

                <div style={styles.scrollContainer}>
                    <table style={{ ...styles.table, backgroundColor: themeColors.mainBackground, marginTop: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ backgroundColor: themeColors.background }}>
                                <th style={{...styles.center, width: '40px', borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                                    <input
                                        type="checkbox"
                                        ref={selectAllOperationsRef}
                                        onChange={() => handleSelectAllOperations(filteredOperations)}
                                    />
                                </th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Date</th>
                                <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Libellé</th>
                                <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Type</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Montant</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Statut</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Mode</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Source</th>
                                <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Dest.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOperations.map((op) => {
                                const isTransfer = op.sourceAccountId !== null && op.destinationAccountId !== null;
                                const formattedMontant = isTransfer 
                                    ? (op.sourceAccountId === selectedAccount.id ? -Math.abs(op.montant) : Math.abs(op.montant))
                                    : op.montant;
                                const isDebit = formattedMontant < 0;

                                return (
                                    <tr
                                        key={op.id}
                                        style={{ cursor: "pointer", backgroundColor: selectedOperationIds.includes(op.id) ? themeColors.navActive : themeColors.mainBackground }}
                                        onClick={() => handleEditOperation(op)}
                                    >
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }} onClick={(e) => { e.stopPropagation(); handleSelectOperation(op.id); }}>
                                            <input type="checkbox" checked={selectedOperationIds.includes(op.id)} readOnly />
                                        </td>
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{new Date(op.date).toLocaleDateString("fr-FR", { timeZone: "UTC" })}</td>
                                        <td style={{ ...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{isTransfer ? op.description : `${categories.find((c) => c.id === op.categorie_id)?.name || "N/A"} - ${op.description}`}</td>
                                        <td style={{ ...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{isTransfer ? 'Transfert' : 'Standard'}</td>
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>
                                            <span style={{ color: isDebit ? themeColors.danger : themeColors.success }}>{formatCurrency(formattedMontant)} €</span>
                                        </td>
                                        <td style={{ ...styles.center, color: op.statut === "Payé" ? themeColors.success : themeColors.danger, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{op.statut}</td>
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{op.paiementMode || "N/A"}</td>
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{isTransfer ? getAccountName(op.sourceAccountId) : "-"}</td>
                                        <td style={{ ...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}` }}>{isTransfer ? getAccountName(op.destinationAccountId) : "-"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
          )}  

          {selectedTab === "cat-banque" && (
              <>
                <h2 style={{ color: themeColors.secondary, marginBottom: '5px' }}>Répartition des opérations par catégorie ({selectedYear})</h2>
                <div style={styles.scrollContainer}>
                    <table style={{ ...styles.tableCompact, backgroundColor: themeColors.mainBackground }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr style={{ backgroundColor: themeColors.background }}>
                        <th style={{...styles.left, ...styles.headerCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Catégorie</th>
                        {monthNamesOperations.map(month => <th key={month} style={{...styles.center, ...styles.headerCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>{month}</th>)}
                        <th style={{...styles.center, ...styles.headerCompact, fontWeight: 'bold', borderBottom: `1px solid ${themeColors.inputBorder}`}}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {aggregatedOperations.map((row, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? themeColors.mainBackground : themeColors.formBackground }}>
                            <td style={{...styles.left, ...styles.cellCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>{row.name}</td>
                            {monthNamesOperations.map(month => (
                            <td key={month} style={{...styles.center, ...styles.cellCompact, borderBottom: `1px solid ${themeColors.inputBorder}`}}>{formatCurrency(row[month])} {row[month] !== 0 && '€'}</td>
                            ))}
                            <td style={{...styles.center, ...styles.cellCompact, fontWeight: 'bold', borderBottom: `1px solid ${themeColors.inputBorder}`}}>{formatCurrency(row.total)} {row.total !== 0 && '€'}</td>
                        </tr>
                        ))}
                    </tbody>
                    {/* ⭐ AJOUT DU PIED DE TABLEAU */}
                    <tfoot>
                        <tr style={{ backgroundColor: themeColors.navActive, fontWeight: 'bold'}}>
                            <td style={{...styles.left, ...styles.cellCompact}}>Total</td>
                            {monthNamesOperations.map(month => (
                                <td key={month} style={{...styles.center, ...styles.cellCompact}}>
                                    {formatCurrency(monthlyTotalsOperations[month])} {monthlyTotalsOperations[month] !== 0 && '€'}
                                </td>
                            ))}
                            <td style={{...styles.center, ...styles.cellCompact}}>{formatCurrency(grandTotalOperations)} €</td>
                        </tr>
                    </tfoot>
                    </table>
                </div>
              </>
          )}

          {selectedTab === "gerer-personnes" && (
            <>
                <div style={styles.fixedContent}>
                    <h2 style={{ color: themeColors.secondary }}>Gérer les personnes</h2>
                    
                    <button 
                        style={{ ...styles.topButton, backgroundColor: themeColors.primary, color: themeColors.textSecondary }} 
                        onClick={() => handlePersonEdit(null)} 
                    >
                        Ajouter une personne
                    </button>
                    
                    {isPersonFormVisible && (
                        <form 
                            onSubmit={handlePersonSubmit} 
                            style={{
                                ...styles.form, 
                                marginBottom: '20px', 
                                marginTop: '20px', 
                                padding: '15px', 
                                border: `1px solid ${themeColors.secondary}`, 
                                backgroundColor: themeColors.formBackground // Fond dynamique
                            }}
                        >
                            <h3 style={{ color: themeColors.text }}>{editingPerson ? "Modifier la personne" : "Nouvelle personne"}</h3>

                            <input
                                style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground }}
                                type="text"
                                placeholder="Nom de la personne"
                                value={personName}
                                onChange={(e) => setPersonName(e.target.value)}
                                required
                            />
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="checkbox"
                                    checked={isManaged}
                                    onChange={(e) => setIsManaged(e.target.checked)}
                                    style={{ width: 'auto' }}
                                />
                                <label style={{ color: themeColors.text }}>Gère les comptes</label>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsPersonFormVisible(false)} 
                                    style={{ ...styles.cancelButton, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                                >
                                Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    style={{ ...styles.saveButton, backgroundColor: themeColors.success, color: themeColors.textSecondary }}
                                >
                                {editingPerson ? "Modifier" : "Ajouter"}
                                </button>
                            </div>
                        </form>
                    )}
              </div>

              <div style={styles.scrollContainer}>
              <table style={{ ...styles.table, backgroundColor: themeColors.mainBackground }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <tr style={{ backgroundColor: themeColors.background }}>
                    <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Nom</th>
                    <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Gère les comptes</th>
                    <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {persons.map((p) => (
                    <tr key={p.id}>
                      <td style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>{p.name}</td>
                      <td style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                        <input
                            type="checkbox"
                            checked={p.is_managed === 1 || p.is_managed === true}
                            onChange={() => {
                                const updatedPerson = {
                                    id: p.id,
                                    name: p.name,
                                    is_managed: !(p.is_managed === 1 || p.is_managed === true),
                                };
                                handlePersonSubmit(null, updatedPerson); 
                            }}
                            disabled={p.id === selectedPerson?.id} 
                        />
                    </td>
                      <td style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                        <button
                            onClick={() => handlePersonEdit(p)}
                            style={{ ...styles.actionButton, backgroundColor: themeColors.accent, color: themeColors.text }}
                        >
                          Modifier
                        </button>
                        <button
                            onClick={() => handleDeleteEntity("persons", p.id, refreshAllData)}
                            style={{ ...styles.actionButton, backgroundColor: themeColors.danger, color: themeColors.textSecondary }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}

          {selectedTab === "gerer-categories" && (
                <div style={styles.tabContentContainer}> 
                    
                    {/* ZONE STATIQUE (ne défile pas) */}
                    <div style={styles.fixedContent}>
                        <h2 style={{ color: themeColors.secondary }}>Gérer les catégories</h2>
                        <button 
                            style={{ ...styles.topButton, backgroundColor: themeColors.primary, color: themeColors.textSecondary }} 
                            onClick={() => handleCategoryEdit(null)} 
                        >
                            Ajouter une catégorie
                        </button>

                        {isCategoryFormVisible && (
                            <form 
                                onSubmit={handleCategorySubmit} 
                                style={{
                                    ...styles.form, 
                                    marginBottom: '20px', 
                                    marginTop: '20px', 
                                    padding: '15px', 
                                    border: `1px solid ${themeColors.secondary}`, 
                                    backgroundColor: themeColors.formBackground
                                }}
                            >
                                <h3 style={{ color: themeColors.text }}>{editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}</h3>

                                {/* Champ Nom */}
                                <input
                                    style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}`, background: themeColors.inputBackground }}
                                    type="text"
                                    placeholder="Nom de la catégorie"
                                    value={categoryName}
                                    onChange={(e) => setCategoryName(e.target.value)}
                                    required
                                />
                                
                                {/* 💡 NOUVEAU BLOC : Boutons Radio pour le Type de Flux */}
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ 
                                        fontWeight: 'bold', 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: themeColors.text 
                                    }}>
                                        Type de flux :
                                    </label>
                                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                        {/* FLOW_TYPES est importé depuis Constants.jsx */}
                                        {FLOW_TYPES.map((type) => (
                                            <label 
                                                key={type.code} 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    cursor: 'pointer', 
                                                    color: themeColors.text 
                                                }}>
                                                <input
                                                    type="radio"
                                                    name="categoryType"
                                                    value={type.code}
                                                    checked={categoryType === type.code} 
                                                    onChange={(e) => setCategoryType(e.target.value)}
                                                    style={{ marginRight: '5px' }}
                                                    required
                                                />
                                                {type.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {/* 💡 FIN NOUVEAU BLOC */}

                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setIsCategoryFormVisible(false)} 
                                        style={{ ...styles.cancelButton, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                                    >
                                      Annuler
                                    </button>
                                    <button 
                                        type="submit" 
                                        style={{ ...styles.saveButton, backgroundColor: themeColors.success, color: themeColors.textSecondary }}
                                    >
                                      {editingCategory ? "Modifier" : "Ajouter"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* ZONE À DÉFILEMENT (liste des catégories) */}
                    <div style={styles.categoryListScroll}>
                        <table style={{ ...styles.table, marginTop: '0', backgroundColor: themeColors.mainBackground }}> 
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <tr style={{ backgroundColor: themeColors.background }}>
                                    <th style={{...styles.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Nom</th>
                                    {/* 💡 NOUVEAU : En-têtes pour les types de flux */}
                                    {FLOW_TYPES.map(type => (
                                        <th key={type.code} style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`, width: '100px'}}>
                                            {type.label}
                                        </th>
                                    ))}
                                    {/* FIN NOUVEAU */}
                                    <th style={{...styles.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleCategories.map((c, index) => (
                                  <CategoryRow
                                      key={c.id}
                                      category={c}
                                      handleCategoryEdit={handleCategoryEdit}
                                      handleToggleCategoryVisibility={handleToggleCategoryVisibility}
                                    // 💡 NOUVEAU : Passage de la fonction de mise à jour du type
                                    handleUpdateCategoryType={handleUpdateCategoryType} 
                                      styles={styles}
                                      themeColors={themeColors}
                                      />
                                ))}                                
                            </tbody>
                        </table>
                      </div>
                </div>                
          )}

          {selectedTab === "graphique" && (
            <>
              <h2 style={{ color: themeColors.secondary }}>Dépenses mensuelles</h2>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke={themeColors.text} />
                  <YAxis stroke={themeColors.text} />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="total"
                    // Couleur dynamique du graphique
                    fill={themeColors.chartBar}
                    name={"Total"}                    
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        <div style={{ ...styles.footer, borderTop: "1px solid rgb(212, 212, 212)" }}>
          {footerContent} 
        </div>
      </div>

      {showFactureModal && (
        <FactureModal
          show={showFactureModal}
          editingFacture={editingFacture}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          selectedPerson={selectedPerson}
          selectedAccount={selectedAccount}
          onSave={handleCloseFactureModal} 
          onDelete={() => {}}
          onCancel={handleCloseFactureModal}
          refreshDashboardData={refreshDashboardData}
          refreshAllData={refreshAllData}
        />
      )}

      {showOperationModal && (
        <OperationModal
          show={showOperationModal}
          editingOperation={editingOperation}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          selectedPerson={selectedPerson}
          selectedAccount={selectedAccount}
          onSave={handleCloseOperationModal} 
          onDelete={() => {}}
          onCancel={handleCloseOperationModal}
          refreshDashboardData={refreshDashboardData}
          refreshAllData={refreshAllData}
        />
      )}
      
      {showRecurrenceModal && (
        <RecurrenceModal
          show={showRecurrenceModal}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          onClose={handleCloseRecurrenceModal}
          onNextStep={handleNextStep}
          refreshAllData={refreshAllData}
        />
      )}

      {showRecurrenceMonthsModal && (
        <RecurrenceMonthsModal
          show={showRecurrenceMonthsModal}
          initialData={dataForMonthsModal}
          isEditing={false}
          onClose={() => handleCloseMonthsModal(null)}
          onSave={handleCloseMonthsModal}
        />
      )}

      {showBatchModal && (
        <BatchUpdateForm 
            type={batchType} 
            onClose={() => setShowBatchModal(false)}
            refreshDashboardData={refreshDashboardData}
        />
      )}
    </div>
  );
}

// ===============================================
// 🎨 STYLES LOCAUX (Adaptés pour la cohésion)
// ===============================================

const styles = {
    container: {
        display: "flex",
        height: "calc(100vh - 60px)",
        marginTop: "0px",
        overflow: "hidden",
    },

    sidebar: {
        width: "220px", 
        padding: "20px 10px 20px 20px",
        overflowY: "auto",
        height: "95%",
        borderRadius: "0px 8px 8px 0px", 
        boxShadow: '2px 0 5px rgba(0,0,0,0.05)',
    },
    
    navList: {
        listStyle: "none",
        padding: 0,
        margin: "10px 0",
    },

    navItem: {
        padding: "10px 15px",
        cursor: "pointer",
        display: "block",
        textDecoration: "none",
        borderRadius: "4px",
        transition: "background-color 0.2s, color 0.2s",
        fontSize: "0.95em",
        marginBottom: "5px",
        fontWeight: "550",
        "&:hover": { 
             backgroundColor: '#f0f0f0',
        }
    },
    
    main: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        margin: "20px 10px 20px 10px", 
        borderRadius: "8px",
        overflow: "hidden", 
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        position: "relative",
        zIndex: 1,
    },

    topButton: {
        border: "none",
        padding: "10px 15px",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "bold",
        transition: "background-color 0.2s",
    },

    creationButtonsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginBottom: "20px",
        paddingBottom: "15px",
    },

    sidebarButton: {
        border: "none",
        padding: "10px",
        borderRadius: "4px",
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        fontWeight: "bold",
        transition: "background-color 0.2s",
    },

    input: {
        padding: "10px",
        fontSize: "16px",
        borderRadius: "4px",
        boxSizing: 'border-box',
    },
    form: { 
        display: "flex", 
        flexDirection: "column", 
        gap: 10,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },

    content: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        borderRadius: '4px',
    },

    scrollContainer: {
        flex: 1,
        overflowY: 'auto',
        border: '1px solid #eee',
        borderRadius: '4px',
        marginTop: '10px',
    },

    center: {
        textAlign: "center",
        padding: "12px 8px", 
    },
    left: {
        textAlign: "left",
        padding: "12px 8px", 
    },
    
    actionButton: {
        border: 'none',
        padding: '5px 10px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
        marginLeft: '5px',
        fontSize: '0.85em',
        transition: 'opacity 0.2s',
        "&:hover": {
            opacity: 0.8,
        }
    },
    saveButton: { 
        padding: '10px 15px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.2s',
    },
    cancelButton: { 
        padding: '10px 15px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.2s',
    },

    footer: { 
        marginTop: "auto", 
        padding: "10px 0 0",
        width: '100%', 
        flexShrink: 0,
    },
    totalsRow: {
        display: "flex",
        justifyContent: "space-around",
        marginBottom: "5px",
        padding: "5px 0",
        borderRadius: '4px',
    },
    corbeilleHeader: {
        padding: '10px 15px',
        borderRadius: '4px',
        marginTop: '20px',
        fontWeight: 'bold',
        transition: 'background-color 0.2s',
    },
    corbeilleContent: {
        borderTop: 'none',
        padding: '15px',
        borderRadius: '0 0 4px 4px',
        marginTop: '0',
    },
    batchActionsBar: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid #DDD',
        marginBottom: '10px',
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },

    modalOverlay: {
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
    },
    modalContent: {
        padding: "30px", 
        borderRadius: "8px",
        minWidth: "450px",
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    modalWarning: {
        color: '#333333',
        padding: '10px',
        borderRadius: '4px',
        marginBottom: '15px',
        textAlign: 'center',
        fontSize: '0.9em',
        fontWeight: 'bold',
    },
    modalActions: { 
        display: "flex", 
        justifyContent: "flex-end", 
        marginTop: "20px", 
        gap: "10px", 
    },

    tabContentContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    fixedContent: {
        flexShrink: 0,
        paddingBottom: '15px',
    },
    categoryListScroll: {
        flexGrow: 1,
        overflowY: 'auto',
        minHeight: '0',
    },
    tableCompact: {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: '10px',
        fontSize: '0.9em',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    cellCompact: {
        padding: "6px 6px",
        borderBottom: `1px solid`,
    },
    headerCompact: {
        padding: "6px 6px",
        fontWeight: 'bold',
        fontSize: '0.9em',
    }
};